import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { XIcon, SparklesIcon, CheckIcon, ChevronDownIcon, ArrowLeftIcon } from 'lucide-react';
import { CatalogItem } from '../../data/catalogItems';
import { ALL_BLUEPRINTS, PRICING_FIELDS, blueprint } from '../../data/productCategories';

/** Rough intent detection — enough to offer the right starting point. */
const HINTS: {
  kind: string;
  words: string[];
}[] = [{
  kind: 'vehicle-rental',
  words: ['toyota', 'nissan', 'prado', 'camry', 'land cruiser', 'patrol', 'bmw', 'mercedes', 'car', 'suv', 'sedan', 'van', 'hiace']
}, {
  kind: 'perfume',
  words: ['perfume', 'eau de', 'edp', 'edt', 'oud', 'fragrance', 'ml', 'sauvage']
}, {
  kind: 'restaurant',
  words: ['burger', 'pizza', 'biryani', 'salad', 'dish', 'platter', 'dessert', 'shawarma']
}, {
  kind: 'clothing',
  words: ['shirt', 'dress', 't-shirt', 'jacket', 'trousers', 'abaya', 'shoes']
}, {
  kind: 'grocery',
  words: ['milk', 'bread', 'eggs', 'rice', 'water', 'pack', 'litre', 'kg']
}, {
  kind: 'electronics',
  words: ['iphone', 'samsung', 'laptop', 'macbook', 'tv', 'headphones', 'gb']
}];
function detect(text: string, fallback: string) {
  const t = text.toLowerCase();
  for (const h of HINTS) if (h.words.some(w => t.includes(w))) return h.kind;
  return fallback;
}

/**
 * "Add my item" — Talab works out what the thing is, then asks only for what
 * that kind of item actually needs. Everything else waits behind More details.
 */
export function AddItemFlow({
  defaultKind,
  categories,
  onCreateCategory,
  onClose,
  onAdd
}: {
  defaultKind: string;
  /** The merchant's own categories, so they never leave this flow to add one. */
  categories?: string[];
  onCreateCategory?: (name: string) => void;
  onClose: () => void;
  onAdd: (item: CatalogItem) => void;
}) {
  const [newCat, setNewCat] = useState('');
  const [creatingCat, setCreatingCat] = useState(false);
  const [step, setStep] = useState<'name' | 'details'>('name');
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState(defaultKind);
  const [intent, setIntent] = useState<'rent' | 'sell'>('rent');
  const [price, setPrice] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [more, setMore] = useState(false);
  const detected = useMemo(() => detect(title, defaultKind), [title, defaultKind]);
  const resolved = detected === 'vehicle-rental' && intent === 'sell' ? 'vehicle-sale' : kind;
  const bp = blueprint(resolved);
  const isVehicle = detected.startsWith('vehicle');
  const extras = bp.detailFields.filter(f => !bp.essentials.includes(f));
  /** Their own categories when they have them, otherwise the trade's defaults. */
  const catOptions = categories?.length ? categories : bp.categories;
  const submit = () => {
    const clean = title.trim() || `New ${bp.itemNoun}`;
    const det = bp.detailFields.filter(f => values[f]?.trim()).map(f => ({
      label: f,
      value: values[f].trim()
    }));
    const missing: string[] = [];
    if (!description.trim()) missing.push('Add a description');
    if (det.length < bp.essentials.length) missing.push('Add key details');
    onAdd({
      id: `new-${Date.now()}`,
      title: clean,
      kind: resolved,
      category: category || catOptions[0],
      status: 'Draft',
      priceLabel: price.trim() ? `AED ${price.trim()}` : 'Price not set',
      image: '',
      images: [],
      description: description.trim(),
      details: det,
      options: [],
      missing: price.trim() ? missing : ['Add a price', ...missing],
      aiScore: Math.max(24, 40 + det.length * 8 + (description.trim() ? 16 : 0)),
      talabPublished: false,
      leads: 0
    });
  };
  return <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-6" data-id="element-962" data-mp="38y6spex87dda">
      <div className="absolute inset-0 bg-dark-navy/40 backdrop-blur-sm" onClick={onClose} data-id="element-963" data-mp="33z3qs3cjln9j" />
      <motion.div initial={{
      opacity: 0,
      y: 16,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} transition={{
      duration: 0.22,
      ease: [0.23, 1, 0.32, 1]
    }} className="relative w-full sm:max-w-xl bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col" data-id="element-964" data-mp="3ioj9qx5u0oeo">
        <header className="flex items-center gap-3 px-6 py-5 border-b border-slate-100" data-id="element-965" data-mp="hby0mql0zaa">
          {step === 'details' && <button onClick={() => setStep('name')} aria-label="Back" className="p-1.5 -ml-1.5 rounded-lg text-slate-400 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-966" data-mp="277046y4s49cy">
              <ArrowLeftIcon className="w-4 h-4" data-id="element-967" data-mp="hrfv7orzg5mw" />
            </button>}
          <div className="min-w-0" data-id="element-968" data-mp="mp7oon89zzt7">
            <h2 className="font-display font-bold text-lg text-dark-navy" data-id="element-969" data-mp="mrlkwzeozi2c">
              {step === 'name' ? 'Add an item' : title.trim() || 'New item'}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5" data-id="element-970" data-mp="cycyj7rwsg2h">
              {step === 'name' ? 'Tell Talab what it is — we’ll ask for the rest.' : `Talab recognised this as ${bp.label.toLowerCase()}. Only what matters is shown.`}
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="ml-auto p-2 rounded-lg text-slate-400 hover:text-dark-navy hover:bg-slate-50 transition-colors duration-150 ease-out" data-id="element-971" data-mp="3l8oac0vbsrdx">
            <XIcon className="w-4 h-4" data-id="element-972" data-mp="1je6yw7sf7bo1" />
          </button>
        </header>

        <div className="px-6 py-6 overflow-y-auto space-y-6" data-id="element-973" data-mp="1oekyd1fyhdag">
          {step === 'name' ? <>
              <div data-id="element-974" data-mp="1mvum1p2i5jq8">
                <label className="block text-sm font-semibold text-dark-navy mb-2" data-id="element-975" data-mp="1hyuiplx1ev2t">
                  What are you adding?
                </label>
                <input autoFocus value={title} onChange={e => {
              setTitle(e.target.value);
              setKind(detect(e.target.value, defaultKind));
            }} placeholder="Toyota Prado 2025 · Dior Sauvage 100ml · Chicken Burger" className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-dark-navy placeholder:text-slate-300 outline-none focus:border-primary-indigo/50 focus:ring-4 focus:ring-primary-indigo/10 transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-976" data-mp="1d0j6j4j9s8hf" />
              </div>

              {title.trim().length > 2 && <div className="rounded-2xl border border-purple-100 bg-purple-50/40 p-4" data-id="element-977" data-mp="2jmn6eywgxxxt">
                  <p className="inline-flex items-center gap-1.5 text-xs font-bold text-ai-purple" data-id="element-978" data-mp="66vf9vyvxynf">
                    <SparklesIcon className="w-3.5 h-3.5" data-id="element-979" data-mp="b5g9nergho3y" />
                    Talab thinks this is {blueprint(detected).label.toLowerCase()}
                  </p>
                  {isVehicle && <div className="flex items-center gap-2 mt-3" data-id="element-980" data-mp="k64oucsl5uif">
                      <span className="text-xs font-semibold text-slate-600" data-id="element-981" data-mp="p4xx9qt0mvbq">
                        Are you
                      </span>
                      {(['rent', 'sell'] as const).map(o => <button key={o} onClick={() => setIntent(o)} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors duration-150 ease-out ${intent === o ? 'bg-primary-indigo text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-primary-indigo/40'}`} data-id="element-982" data-mp="ytzgdpuuxstx">
                          {o === 'rent' ? 'Renting it' : 'Selling it'}
                        </button>)}
                    </div>}
                  <div className="mt-3" data-id="element-983" data-mp="wrjfquz6ekrk">
                    <select value={kind} onChange={e => setKind(e.target.value)} className="text-xs font-semibold text-slate-500 bg-transparent outline-none cursor-pointer" data-id="element-984" data-mp="wq8f34111t4o">
                      {ALL_BLUEPRINTS.map(b => <option key={b.id} value={b.id} data-id="element-985" data-mp="2txh3jmzq52">
                          Change category — {b.label}
                        </option>)}
                    </select>
                  </div>
                </div>}
            </> : <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-986" data-mp="3hfxt5etwpawb">
                <div data-id="element-987" data-mp="3df460noag04h">
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5" data-id="element-988" data-mp="3dfnlzyudoefv">
                    Category
                  </label>
                  {creatingCat ? <div className="flex items-center gap-2" data-id="element-989" data-mp="3840avtcda5o5">
                      <input autoFocus value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="Category name" className="flex-1 min-w-0 px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-dark-navy placeholder:text-slate-300 outline-none focus:border-primary-indigo/50 transition-colors duration-150 ease-out" data-id="element-990" data-mp="2teax8yn3896z" />
                      <button disabled={!newCat.trim()} onClick={() => {
                  const n = newCat.trim();
                  onCreateCategory?.(n);
                  setCategory(n);
                  setNewCat('');
                  setCreatingCat(false);
                }} className="px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-dark-navy hover:border-primary-indigo/40 disabled:opacity-40 transition-colors duration-150 ease-out flex-shrink-0" data-id="element-991" data-mp="3h3n59v6oib7p">
                        Create
                      </button>
                    </div> : <>
                      <select value={category || catOptions[0]} onChange={e => setCategory(e.target.value)} className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-dark-navy outline-none focus:border-primary-indigo/50 transition-colors duration-150 ease-out" data-id="element-992" data-mp="3cv8uxkttdbax">
                        {catOptions.map(c => <option key={c} value={c} data-id="element-993" data-mp="165uagktmpm85">
                            {c}
                          </option>)}
                      </select>
                      <button onClick={() => setCreatingCat(true)} className="mt-1.5 text-[12px] font-bold text-primary-indigo hover:text-indigo-700 transition-colors duration-150 ease-out" data-id="element-994" data-mp="2qlscg2h6x5xu">
                        + Create new category
                      </button>
                    </>}
                </div>
                <div data-id="element-995" data-mp="39gh3dl83xfu0">
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5" data-id="element-996" data-mp="35dayoi0n7wpn">
                    {PRICING_FIELDS[bp.pricing].lead}
                  </label>
                  <div className="relative" data-id="element-997" data-mp="3japss01ucsny">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400" data-id="element-998" data-mp="3edfvxzc4heq3">
                      AED
                    </span>
                    <input value={price} onChange={e => setPrice(e.target.value)} placeholder="0" className="w-full pl-14 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-dark-navy placeholder:text-slate-300 outline-none focus:border-primary-indigo/50 focus:ring-4 focus:ring-primary-indigo/10 transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-999" data-mp="1jzofafvdlul5" />
                  </div>
                </div>
                {bp.essentials.map(f => <div key={f} data-id="element-1000" data-mp="6tl79q2khgyl">
                    <label className="block text-xs font-semibold text-slate-500 mb-1.5" data-id="element-1001" data-mp="btz389qw90g9">
                      {f}
                    </label>
                    <input value={values[f] ?? ''} onChange={e => setValues(s => ({
                ...s,
                [f]: e.target.value
              }))} className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-dark-navy outline-none focus:border-primary-indigo/50 focus:ring-4 focus:ring-primary-indigo/10 transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-1002" data-mp="glquynlsdc7z" />
                  </div>)}
              </div>

              <div data-id="element-1003" data-mp="qp5qsn09uwhx">
                <div className="flex items-center justify-between mb-1.5" data-id="element-1004" data-mp="qpnwsbcqkp31">
                  <label className="block text-xs font-semibold text-slate-500" data-id="element-1005" data-mp="1nfex4r7pl6gr">
                    Description
                  </label>
                  <button onClick={() => setDescription(`${title.trim() || bp.itemNoun} — ${bp.essentials.map(f => values[f]).filter(Boolean).join(' · ')}. Available now from our ${bp.label.toLowerCase()} range.`)} className="inline-flex items-center gap-1 text-[11px] font-bold text-ai-purple hover:text-purple-700 transition-colors duration-150 ease-out" data-id="element-1006" data-mp="1ihd5hhw7vyto">
                    <SparklesIcon className="w-3 h-3" data-id="element-1007" data-mp="1qxaskf698fhh" />
                    Write this for me
                  </button>
                </div>
                <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-dark-navy placeholder:text-slate-300 outline-none focus:border-primary-indigo/50 focus:ring-4 focus:ring-primary-indigo/10 transition-[border-color,box-shadow] duration-150 ease-out resize-none" placeholder="What a customer should know first." data-id="element-1008" data-mp="1cykw91r9eju6" />
              </div>

              {extras.length > 0 && <div className="border-t border-slate-100 pt-4" data-id="element-1009" data-mp="2nkfimnmyc39a">
                  <button onClick={() => setMore(m => !m)} className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-600 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-1010" data-mp="2se1tqbdgvz2z">
                    More details
                    <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ease-out ${more ? 'rotate-180' : ''}`} data-id="element-1011" data-mp="qmrn8hutavsf" />
                  </button>
                  {more && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4" data-id="element-1012" data-mp="3u6w12khtua8y">
                      {extras.map(f => <div key={f} data-id="element-1013" data-mp="7xbljr7e4xaf">
                          <label className="block text-xs font-semibold text-slate-500 mb-1.5" data-id="element-1014" data-mp="7ycu9p0xp7oz">
                            {f}
                          </label>
                          <input value={values[f] ?? ''} onChange={e => setValues(s => ({
                  ...s,
                  [f]: e.target.value
                }))} className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-dark-navy outline-none focus:border-primary-indigo/50 transition-colors duration-150 ease-out" data-id="element-1015" data-mp="1jci4v0vnkfta" />
                        </div>)}
                    </div>}
                </div>}
            </>}
        </div>

        <footer className="mt-auto flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50/60" data-id="element-1016" data-mp="17pyoysjubs4z">
          <button onClick={onClose} className="px-4 py-2.5 text-sm font-semibold text-slate-500 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-1017" data-mp="1cmp6zlf7gxt1">
            Cancel
          </button>
          {step === 'name' ? <button disabled={title.trim().length < 2} onClick={() => {
          setKind(detected);
          setStep('details');
        }} className="px-5 py-2.5 bg-primary-indigo text-white text-sm font-bold rounded-xl hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150 ease-out" data-id="element-1018" data-mp="oxo2285l04si">
              Continue
            </button> : <button onClick={submit} className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-indigo text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-1019" data-mp="2jawh5xv84rq6">
              <CheckIcon className="w-4 h-4" data-id="element-1020" data-mp="2aiazkl3bwr2x" />
              Add to catalogue
            </button>}
        </footer>
      </motion.div>
    </div>;
}